/**
 * Practice level editor (admin only). Wraps the SpeedRun layout editor and adds
 * balls, mechanics toggles, and guide-step overlay authoring.
 */
import { mountAdminSpeedrunEditor } from "./admin-speedrun-editor";
import {
    autoMessageSide,
    PRACTICE_CATEGORIES,
    normalizePracticeLevelData,
    practiceLevelToJson,
    emptyPracticeLevel,
    type PracticeBallDef,
    type PracticeMechanics,
    type PracticeGuideStepDef,
    type PracticeGuideZoneDef,
    type PracticeLevelDef,
} from "./practice-levels";
import {
    fetchPracticeLevels,
    saveAdminPracticeLevel,
    fetchPracticeEditorDrafts,
    fetchPracticeEditorDraft,
    savePracticeEditorDraft,
    type PracticeEditorDraftSummary,
} from "../lib/cloud";

export type AdminPracticeEditorOptions = {
    onLevelsChanged: () => void;
    permissions?: {
        publish?: boolean;
        overwrite?: boolean;
    };
};

export type AdminPracticeEditorApi = {
    destroy(): void;
    loadLevel(def: PracticeLevelDef): void;
    refitCanvas(): void;
    getDef(): PracticeLevelDef;
};

function clamp(v: number, a: number, b: number) {
    return Math.max(a, Math.min(b, v));
}

const MECH_DEFS: { key: keyof PracticeMechanics; label: string; defaultOn?: boolean }[] = [
    { key: "colorRush", label: "Color Rush" },
    { key: "aim", label: "AIM" },
    { key: "wasd", label: "WASD" },
    { key: "magnet", label: "Magnet" },
    { key: "progression", label: "Progression" },
    { key: "bounceBoost", label: "Bounce Boost", defaultOn: true },
    { key: "ballCollisions", label: "Ball Collisions" },
];

const GUIDE_COLORS = ["#4af", "#f4a", "#af4", "#fa4", "#a4f", "#4fa", "#ff9", "#f90"];

export function mountAdminPracticeEditor(
    mount: HTMLElement,
    opts: AdminPracticeEditorOptions,
): AdminPracticeEditorApi {
    mount.replaceChildren();

    const canPublish = !!opts.permissions?.publish;
    const canOverwrite = !!opts.permissions?.overwrite;

    // ---- mutable state ----
    let balls: PracticeBallDef[] = [];
    let mechanics: PracticeMechanics = { bounceBoost: true };
    let guideSteps: PracticeGuideStepDef[] = [];
    let guideMode = false;
    let activePracticeTool: "none" | "ball" | "selectBall" | "eraseBall" = "none";
    let selectedBallIdx = -1;
    let selectedGuideStepIdx = -1;
    let guideDragAnchor: { nx: number; ny: number } | null = null;
    let guideDragPreview: { ax: number; ay: number; bx: number; by: number } | null = null;
    let editorDestroyed = false;

    // ---- DOM helpers ----
    const mkBtn = (label: string, onClick: () => void, extraClass = "") => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = `btn${extraClass ? " " + extraClass : ""}`;
        b.textContent = label;
        b.onclick = onClick;
        return b;
    };

    const mkSection = (title: string) => {
        const sec = document.createElement("section");
        sec.className = "adminToolbarSection";
        const h = document.createElement("div");
        h.className = "adminToolbarSectionTitle";
        h.textContent = title;
        const body = document.createElement("div");
        body.className = "adminToolbarSectionBody";
        sec.append(h, body);
        return { sec, body };
    };

    // ---- root layout: canvas left, all controls right ----
    const root = document.createElement("div");
    root.className = "adminPracticeRoot";

    const editorBody = document.createElement("div");
    editorBody.className = "adminPracticeEditorBody";

    const layoutMount = document.createElement("div");
    layoutMount.id = "adminPracticeLayoutMount";
    layoutMount.className = "adminPracticeLayoutMount";
    editorBody.appendChild(layoutMount);

    const rightRail = document.createElement("aside");
    rightRail.className = "adminPracticeRightRail";
    rightRail.setAttribute("aria-label", "Practice Creator tools");

    const practiceTools = document.createElement("div");
    practiceTools.className = "adminPracticeTools";

    const levelSec = mkSection("Level");
    const saveDraftBtn = mkBtn("Save", () => openSaveDraftDialog());
    const loadDraftBtn = mkBtn("Load", () => openLoadDraftDialog());
    const publishBtn = mkBtn("Publish", () => openPublishDialog());
    if (!canPublish && !canOverwrite) {
        publishBtn.disabled = true;
        publishBtn.title = "Requires Publish Practice Levels permission";
    }
    levelSec.body.append(saveDraftBtn, loadDraftBtn, publishBtn);

    const testSec = mkSection("Test");
    const playBtn = mkBtn("Play", () => runPlay());
    testSec.body.append(playBtn);

    const guideSec = mkSection("Guide");
    const guideToggleBtn = mkBtn("Guide", () => toggleGuideMode());
    guideSec.body.append(guideToggleBtn);

    const toolsSec = mkSection("Tools");
    const ballBtn = mkBtn("Ball", () => setPracticeTool("ball"));
    const selectBallBtn = mkBtn("Select Ball", () => setPracticeTool("selectBall"));
    const eraseBallBtn = mkBtn("Erase Ball", () => setPracticeTool("eraseBall"));
    toolsSec.body.append(ballBtn, selectBallBtn, eraseBallBtn);

    const mechSec = mkSection("Mechanics");
    const mechCheckboxes = new Map<keyof PracticeMechanics, HTMLInputElement>();
    for (const { key, label, defaultOn } of MECH_DEFS) {
        const row = document.createElement("label");
        row.className = "adminPracticeMechRow";
        const cb = document.createElement("input");
        cb.type = "checkbox";
        cb.checked = !!defaultOn;
        cb.addEventListener("change", () => {
            (mechanics as Record<string, unknown>)[key] = cb.checked;
        });
        mechCheckboxes.set(key, cb);
        row.append(cb, document.createTextNode(" " + label));
        mechSec.body.append(row);
    }

    practiceTools.append(levelSec.sec, testSec.sec, guideSec.sec, toolsSec.sec, mechSec.sec);

    const sidePanel = document.createElement("div");
    sidePanel.className = "adminPracticeSidePanel hidden";

    const layoutToolsHost = document.createElement("div");
    layoutToolsHost.className = "adminPracticeLayoutToolsHost";

    rightRail.append(sidePanel, practiceTools, layoutToolsHost);
    root.append(editorBody, rightRail);
    mount.appendChild(root);

    // ---- mount embedded speedrun editor (sandbox) ----
    const layoutApi = mountAdminSpeedrunEditor(layoutMount, {
        variant: "sandbox",
        onLevelsChanged: () => {},
        permissions: { publish: false, overwrite: false },
    });

    // Move SpeedRun toolbar into the right rail so all buttons live on one side.
    const srRoot = layoutMount.querySelector(".adminSpeedrunRoot") as HTMLElement | null;
    const srToolbar = layoutMount.querySelector(".adminSpeedrunToolbar") as HTMLElement | null;
    if (srToolbar) {
        layoutToolsHost.appendChild(srToolbar);
        srToolbar.classList.add("adminPracticeEmbeddedToolbar");
    }
    if (srRoot) srRoot.classList.add("adminPracticeLayoutOnly");

    // Hide the sandbox editor's own Level (empty) + Test (Play) sections — we provide those.
    for (const sec of layoutToolsHost.querySelectorAll(".adminToolbarSection")) {
        const titleEl = sec.querySelector(".adminToolbarSectionTitle");
        const title = titleEl?.textContent?.trim() ?? "";
        if (title === "Test" || title === "Level") {
            (sec as HTMLElement).style.display = "none";
        }
    }

    // Refit after toolbar relocate so the canvas fills the left column.
    requestAnimationFrame(() => {
        if (!editorDestroyed) layoutApi.refitCanvas();
    });

    // ---- overlays ----
    const canvasWrap = layoutMount.querySelector(".adminCanvasWrap") as HTMLElement | null;
    let ballOverlay: HTMLCanvasElement | null = null;
    let guideOverlay: HTMLCanvasElement | null = null;
    let overlayRo: ResizeObserver | null = null;

    if (canvasWrap) {
        // ensure relative positioning for absolute overlays
        if (getComputedStyle(canvasWrap).position === "static") {
            canvasWrap.style.position = "relative";
        }

        ballOverlay = document.createElement("canvas");
        ballOverlay.className = "adminPracticeOverlayCanvas adminPracticeBallOverlay";
        ballOverlay.style.cssText = "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;";

        guideOverlay = document.createElement("canvas");
        guideOverlay.className = "adminPracticeOverlayCanvas adminPracticeGuideOverlay";
        guideOverlay.style.cssText = "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;";

        canvasWrap.append(ballOverlay, guideOverlay);

        syncOverlaySize();

        overlayRo = new ResizeObserver(() => {
            if (!editorDestroyed) syncOverlaySize();
        });
        overlayRo.observe(canvasWrap);
    }

    // ---- overlay sizing ----
    function syncOverlaySize() {
        if (!canvasWrap) return;
        const w = canvasWrap.offsetWidth;
        const h = canvasWrap.offsetHeight;
        if (ballOverlay) { ballOverlay.width = w; ballOverlay.height = h; }
        if (guideOverlay) { guideOverlay.width = w; guideOverlay.height = h; }
        paintBallOverlay();
        paintGuideOverlay();
    }

    // ---- tool management ----
    function setPracticeTool(t: typeof activePracticeTool) {
        if (guideMode) {
            guideMode = false;
            guideToggleBtn.classList.remove("green");
            canvasWrap?.classList.remove("adminPracticeDim");
            guideDragAnchor = null;
            guideDragPreview = null;
            selectedGuideStepIdx = -1;
            syncGuidePointerEvents();
        }
        activePracticeTool = t;
        syncToolButtons();
        syncBallPointerEvents();
        syncSidePanel();
        paintGuideOverlay();
    }

    function syncToolButtons() {
        ballBtn.classList.toggle("green", activePracticeTool === "ball");
        selectBallBtn.classList.toggle("green", activePracticeTool === "selectBall");
        eraseBallBtn.classList.toggle("green", activePracticeTool === "eraseBall");
    }

    function syncBallPointerEvents() {
        if (!ballOverlay) return;
        const needEvents =
            activePracticeTool === "ball" ||
            activePracticeTool === "selectBall" ||
            activePracticeTool === "eraseBall";
        ballOverlay.style.pointerEvents = needEvents ? "auto" : "none";
    }

    function syncGuidePointerEvents() {
        if (!guideOverlay) return;
        guideOverlay.style.pointerEvents = guideMode ? "auto" : "none";
    }

    // ---- guide mode ----
    function toggleGuideMode() {
        guideMode = !guideMode;
        guideToggleBtn.classList.toggle("green", guideMode);
        if (guideMode) {
            canvasWrap?.classList.add("adminPracticeDim");
            activePracticeTool = "none";
            selectedBallIdx = -1;
            syncToolButtons();
            syncBallPointerEvents();
            if (selectedGuideStepIdx < 0 && guideSteps.length > 0) {
                selectedGuideStepIdx = guideSteps.length - 1;
            }
        } else {
            canvasWrap?.classList.remove("adminPracticeDim");
            guideDragAnchor = null;
            guideDragPreview = null;
            selectedGuideStepIdx = -1;
        }
        syncGuidePointerEvents();
        syncSidePanel();
        paintBallOverlay();
        paintGuideOverlay();
    }

    // ---- ball overlay painting ----
    function paintBallOverlay() {
        if (!ballOverlay) return;
        const ctx = ballOverlay.getContext("2d");
        if (!ctx) return;
        const W = ballOverlay.width;
        const H = ballOverlay.height;
        ctx.clearRect(0, 0, W, H);
        for (let i = 0; i < balls.length; i++) {
            const b = balls[i];
            const px = b.nx * W;
            const py = b.ny * H;
            const r = (b.radius / 700) * H;
            const selected = i === selectedBallIdx;
            ctx.beginPath();
            ctx.arc(px, py, r, 0, Math.PI * 2);
            ctx.fillStyle = selected ? "rgba(255,200,0,0.65)" : "rgba(255,80,80,0.55)";
            ctx.fill();
            ctx.strokeStyle = selected ? "#ffd700" : "#ff5050";
            ctx.lineWidth = selected ? 3 : 2;
            ctx.stroke();
            // direction arrow
            const angleRad = (b.angleDeg * Math.PI) / 180;
            const arrowLen = r + 10;
            const ax = px + Math.cos(angleRad) * arrowLen;
            const ay = py + Math.sin(angleRad) * arrowLen;
            ctx.beginPath();
            ctx.moveTo(px, py);
            ctx.lineTo(ax, ay);
            ctx.strokeStyle = "#fff";
            ctx.lineWidth = 2;
            ctx.stroke();
            // arrowhead
            const headLen = 6;
            const headAngle = Math.PI / 6;
            ctx.beginPath();
            ctx.moveTo(ax, ay);
            ctx.lineTo(
                ax - headLen * Math.cos(angleRad - headAngle),
                ay - headLen * Math.sin(angleRad - headAngle),
            );
            ctx.moveTo(ax, ay);
            ctx.lineTo(
                ax - headLen * Math.cos(angleRad + headAngle),
                ay - headLen * Math.sin(angleRad + headAngle),
            );
            ctx.strokeStyle = "#fff";
            ctx.lineWidth = 2;
            ctx.stroke();
        }
    }

    // ---- guide overlay painting ----
    function paintGuideOverlay() {
        if (!guideOverlay) return;
        const ctx = guideOverlay.getContext("2d");
        if (!ctx) return;
        const W = guideOverlay.width;
        const H = guideOverlay.height;
        ctx.clearRect(0, 0, W, H);
        if (!guideMode) return;

        for (let i = 0; i < guideSteps.length; i++) {
            const z = guideSteps[i].zone;
            const x = z.nx * W;
            const y = z.ny * H;
            const w = z.nw * W;
            const h = z.nh * H;
            const col = GUIDE_COLORS[i % GUIDE_COLORS.length];
            const selected = i === selectedGuideStepIdx;

            ctx.fillStyle = col + (selected ? "55" : "33");
            ctx.fillRect(x, y, w, h);
            ctx.strokeStyle = selected ? "#fff" : col;
            ctx.lineWidth = selected ? 3 : 2;
            ctx.setLineDash(selected ? [] : [6, 3]);
            ctx.strokeRect(x, y, w, h);
            ctx.setLineDash([]);

            ctx.fillStyle = col;
            const fontSize = Math.max(12, Math.min(24, h * 0.45));
            ctx.font = `bold ${fontSize}px sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(String(i + 1), x + w / 2, y + h / 2);
        }

        // draw drag preview
        if (guideDragPreview) {
            const { ax, ay, bx, by } = guideDragPreview;
            const px = Math.min(ax, bx) * W;
            const py = Math.min(ay, by) * H;
            const pw = Math.abs(bx - ax) * W;
            const ph = Math.abs(by - ay) * H;
            ctx.fillStyle = "rgba(255,255,255,0.08)";
            ctx.fillRect(px, py, pw, ph);
            ctx.strokeStyle = "#fff";
            ctx.lineWidth = 1;
            ctx.setLineDash([4, 4]);
            ctx.strokeRect(px, py, pw, ph);
            ctx.setLineDash([]);
        }
    }

    // ---- hit testing ----
    function hitBall(nx: number, ny: number): number {
        if (!ballOverlay) return -1;
        const H = ballOverlay.height;
        const W = ballOverlay.width;
        for (let i = balls.length - 1; i >= 0; i--) {
            const b = balls[i];
            const r = (b.radius / 700) * H;
            const dx = (nx - b.nx) * W;
            const dy = (ny - b.ny) * H;
            if (Math.hypot(dx, dy) <= r + 4) return i;
        }
        return -1;
    }

    function hitGuideStep(nx: number, ny: number): number {
        for (let i = guideSteps.length - 1; i >= 0; i--) {
            const z = guideSteps[i].zone;
            if (nx >= z.nx && nx <= z.nx + z.nw && ny >= z.ny && ny <= z.ny + z.nh) return i;
        }
        return -1;
    }

    // ---- guide overlay events ----
    if (guideOverlay) {
        guideOverlay.addEventListener("pointerdown", (e) => {
            if (!guideMode || editorDestroyed) return;
            const rect = guideOverlay!.getBoundingClientRect();
            const nx = clamp((e.clientX - rect.left) / rect.width, 0, 1);
            const ny = clamp((e.clientY - rect.top) / rect.height, 0, 1);
            const hit = hitGuideStep(nx, ny);
            if (hit >= 0) {
                selectedGuideStepIdx = hit;
                syncSidePanel();
                paintGuideOverlay();
                return;
            }
            guideDragAnchor = { nx, ny };
            try { guideOverlay!.setPointerCapture(e.pointerId); } catch { /* */ }
        });

        guideOverlay.addEventListener("pointermove", (e) => {
            if (!guideDragAnchor || editorDestroyed) return;
            const rect = guideOverlay!.getBoundingClientRect();
            const nx = clamp((e.clientX - rect.left) / rect.width, 0, 1);
            const ny = clamp((e.clientY - rect.top) / rect.height, 0, 1);
            guideDragPreview = { ax: guideDragAnchor.nx, ay: guideDragAnchor.ny, bx: nx, by: ny };
            paintGuideOverlay();
        });

        guideOverlay.addEventListener("pointerup", (e) => {
            if (!guideDragAnchor || editorDestroyed) return;
            const anchor = guideDragAnchor;
            guideDragAnchor = null;
            guideDragPreview = null;
            try { guideOverlay!.releasePointerCapture(e.pointerId); } catch { /* */ }
            const rect = guideOverlay!.getBoundingClientRect();
            const nx = clamp((e.clientX - rect.left) / rect.width, 0, 1);
            const ny = clamp((e.clientY - rect.top) / rect.height, 0, 1);
            const nw = Math.abs(nx - anchor.nx);
            const nh = Math.abs(ny - anchor.ny);
            if (nw > 0.03 && nh > 0.03) {
                const zone: PracticeGuideZoneDef = {
                    nx: Math.min(anchor.nx, nx),
                    ny: Math.min(anchor.ny, ny),
                    nw,
                    nh,
                };
                guideSteps.push({ zone, message: "", messageSide: autoMessageSide(zone) });
                selectedGuideStepIdx = guideSteps.length - 1;
                selectedBallIdx = -1;
                syncSidePanel();
            }
            paintGuideOverlay();
        });
    }

    // ---- ball overlay events ----
    if (ballOverlay) {
        ballOverlay.addEventListener("pointerdown", (e) => {
            if (editorDestroyed) return;
            const rect = ballOverlay!.getBoundingClientRect();
            const nx = clamp((e.clientX - rect.left) / rect.width, 0, 1);
            const ny = clamp((e.clientY - rect.top) / rect.height, 0, 1);
            if (activePracticeTool === "ball") {
                balls.push({ nx, ny, angleDeg: 180, speed: 1, radius: 14 });
                selectedBallIdx = balls.length - 1;
                setPracticeTool("selectBall");
                syncSidePanel();
                paintBallOverlay();
            } else if (activePracticeTool === "selectBall") {
                selectedBallIdx = hitBall(nx, ny);
                syncSidePanel();
                paintBallOverlay();
            } else if (activePracticeTool === "eraseBall") {
                const hit = hitBall(nx, ny);
                if (hit >= 0) {
                    balls.splice(hit, 1);
                    if (selectedBallIdx >= balls.length) selectedBallIdx = balls.length - 1;
                    syncSidePanel();
                    paintBallOverlay();
                }
            }
        });
    }

    // ---- side panel ----
    function syncSidePanel() {
        sidePanel.replaceChildren();
        // Guide mode owns the panel so a leftover ball selection cannot hide it.
        if (guideMode) {
            if (selectedGuideStepIdx >= 0 && selectedGuideStepIdx < guideSteps.length) {
                buildGuideStepPanel(selectedGuideStepIdx);
            } else {
                buildGuideHintPanel();
            }
            sidePanel.classList.remove("hidden");
            requestAnimationFrame(() => {
                sidePanel.scrollIntoView({ block: "nearest", behavior: "smooth" });
            });
            return;
        }
        if (selectedBallIdx >= 0 && selectedBallIdx < balls.length) {
            buildBallPanel(selectedBallIdx);
            sidePanel.classList.remove("hidden");
            requestAnimationFrame(() => {
                sidePanel.scrollIntoView({ block: "nearest", behavior: "smooth" });
            });
        } else {
            sidePanel.classList.add("hidden");
        }
    }

    function buildGuideHintPanel() {
        const title = document.createElement("div");
        title.className = "adminPracticeSidePanelTitle";
        title.textContent = "Guide";
        const hint = document.createElement("p");
        hint.className = "accessHint";
        hint.style.margin = "0";
        hint.style.fontSize = "11px";
        hint.textContent =
            guideSteps.length === 0
                ? "Drag on the dimmed canvas to create a guide zone. A message panel opens after each zone."
                : "Click a zone to edit its message, side, and order — or drag to add another zone.";
        sidePanel.append(title, hint);
    }

    function buildBallPanel(idx: number) {
        const b = balls[idx];

        const title = document.createElement("div");
        title.className = "adminPracticeSidePanelTitle";
        title.textContent = `Ball ${idx + 1}`;
        sidePanel.appendChild(title);

        const mkSliderRow = (
            label: string,
            min: number,
            max: number,
            step: number,
            val: number,
            onChange: (v: number) => void,
        ) => {
            const row = document.createElement("div");
            row.className = "adminPracticeSidePanelRow";
            const lab = document.createElement("div");
            lab.className = "formLabel";
            lab.textContent = label;
            const sl = document.createElement("input");
            sl.type = "range";
            sl.min = String(min);
            sl.max = String(max);
            sl.step = String(step);
            sl.value = String(val);
            const valSpan = document.createElement("span");
            valSpan.className = "adminPracticeSidePanelValue";
            valSpan.textContent = String(val);
            sl.addEventListener("input", () => {
                const v = Number(sl.value);
                valSpan.textContent = String(v);
                onChange(v);
            });
            row.append(lab, sl, valSpan);
            sidePanel.appendChild(row);
            return sl;
        };

        mkSliderRow("Angle (°)", 0, 359, 1, b.angleDeg, (v) => {
            balls[idx].angleDeg = v;
            paintBallOverlay();
        });
        mkSliderRow("Speed", 0.25, 3, 0.05, b.speed, (v) => {
            balls[idx].speed = v;
        });
        mkSliderRow("Radius (px)", 6, 48, 1, b.radius, (v) => {
            balls[idx].radius = v;
            paintBallOverlay();
        });

        const delBtn = mkBtn("Delete Ball", () => {
            balls.splice(idx, 1);
            selectedBallIdx = -1;
            syncSidePanel();
            paintBallOverlay();
        });
        sidePanel.appendChild(delBtn);
    }

    function buildGuideStepPanel(idx: number) {
        const step = guideSteps[idx];

        const title = document.createElement("div");
        title.className = "adminPracticeSidePanelTitle";
        title.textContent = `Step ${idx + 1} of ${guideSteps.length}`;
        sidePanel.appendChild(title);

        const msgLab = document.createElement("div");
        msgLab.className = "formLabel";
        msgLab.textContent = "Message";
        const msgArea = document.createElement("textarea");
        msgArea.className = "formInput adminPracticeGuideMsg";
        msgArea.rows = 4;
        msgArea.maxLength = 500;
        msgArea.placeholder = "Instructions shown to the player…";
        msgArea.value = step.message ?? "";
        msgArea.addEventListener("input", () => { guideSteps[idx].message = msgArea.value; });

        const sideLab = document.createElement("div");
        sideLab.className = "formLabel";
        sideLab.textContent = "Message side";
        const sideSel = document.createElement("select");
        sideSel.className = "formInput";
        for (const s of ["top", "bottom", "left", "right"] as const) {
            const o = document.createElement("option");
            o.value = s;
            o.textContent = s.charAt(0).toUpperCase() + s.slice(1);
            o.selected = step.messageSide === s;
            sideSel.appendChild(o);
        }
        sideSel.addEventListener("change", () => {
            guideSteps[idx].messageSide = sideSel.value as typeof guideSteps[0]["messageSide"];
        });

        const moveUpBtn = mkBtn("↑ Up", () => {
            [guideSteps[idx - 1], guideSteps[idx]] = [guideSteps[idx], guideSteps[idx - 1]];
            selectedGuideStepIdx = idx - 1;
            syncSidePanel();
            paintGuideOverlay();
        });
        moveUpBtn.disabled = idx === 0;

        const moveDownBtn = mkBtn("↓ Down", () => {
            [guideSteps[idx], guideSteps[idx + 1]] = [guideSteps[idx + 1], guideSteps[idx]];
            selectedGuideStepIdx = idx + 1;
            syncSidePanel();
            paintGuideOverlay();
        });
        moveDownBtn.disabled = idx === guideSteps.length - 1;

        const delBtn = mkBtn("Delete Step", () => {
            guideSteps.splice(idx, 1);
            selectedGuideStepIdx = -1;
            syncSidePanel();
            paintGuideOverlay();
        });

        const orderRow = document.createElement("div");
        orderRow.className = "adminLevelRowBtns";
        orderRow.append(moveUpBtn, moveDownBtn, delBtn);

        sidePanel.append(msgLab, msgArea, sideLab, sideSel, orderRow);
    }

    // ---- getDef ----
    function getDef(): PracticeLevelDef {
        const layout = layoutApi.getDef();
        return normalizePracticeLevelData({ ...layout, balls, mechanics, guideSteps });
    }

    // ---- play ----
    function runPlay() {
        document.dispatchEvent(
            new CustomEvent("arcade-admin-practice-play", {
                detail: { def: getDef() },
            }),
        );
    }

    // ---- apply loaded def ----
    function applyDef(def: PracticeLevelDef) {
        balls = (def.balls ?? []).map((b) => ({ ...b }));
        mechanics = { ...(def.mechanics ?? { bounceBoost: true }) };
        guideSteps = (def.guideSteps ?? []).map((s) => ({ ...s, zone: { ...s.zone } }));
        selectedBallIdx = -1;
        selectedGuideStepIdx = -1;
        guideDragAnchor = null;
        guideDragPreview = null;
        for (const { key } of MECH_DEFS) {
            const cb = mechCheckboxes.get(key);
            if (cb) cb.checked = !!(mechanics as Record<string, unknown>)[key];
        }
        syncSidePanel();
        paintBallOverlay();
        paintGuideOverlay();
    }

    // ---- publish dialog ----
    function openPublishDialog() {
        void (async () => {
            let existingLevels: { levelIndex: number }[] = [];
            try {
                existingLevels = await fetchPracticeLevels();
            } catch { /* ok */ }
            const next = existingLevels.length
                ? Math.max(...existingLevels.map((l) => l.levelIndex)) + 1
                : 1;

            const wrap = document.createElement("div");
            wrap.className = "adminSaveDialogOverlay";
            const box = document.createElement("div");
            box.className = "adminSaveDialog modeBox";
            box.innerHTML = "<h3>Publish Practice level</h3>";

            const hint = document.createElement("p");
            hint.className = "accessHint";
            hint.textContent = "Publish makes this level official for all players.";
            box.appendChild(hint);

            const addField = (labelText: string) => {
                const lab = document.createElement("label");
                lab.className = "formLabel";
                lab.textContent = labelText;
                box.appendChild(lab);
            };

            addField("Display name");
            const nameInp = document.createElement("input");
            nameInp.type = "text";
            nameInp.className = "formInput";
            nameInp.maxLength = 120;
            nameInp.placeholder = "Title shown in the level picker";
            box.appendChild(nameInp);

            addField("Description (optional)");
            const descInp = document.createElement("textarea");
            descInp.className = "formInput";
            descInp.rows = 2;
            descInp.maxLength = 500;
            box.appendChild(descInp);

            addField("Category");
            const catSel = document.createElement("select");
            catSel.className = "formInput";
            for (const c of PRACTICE_CATEGORIES) {
                const o = document.createElement("option");
                o.value = c;
                o.textContent = c;
                catSel.appendChild(o);
            }
            box.appendChild(catSel);

            addField("Publish slot");
            const slotSel = document.createElement("select");
            slotSel.className = "formInput";
            const oNew = document.createElement("option");
            oNew.value = "__new__";
            oNew.textContent = canPublish
                ? `Add new level (→ ${next})`
                : `Add new level (→ ${next}) — needs Publish permission`;
            oNew.disabled = !canPublish;
            slotSel.appendChild(oNew);
            for (const l of existingLevels) {
                const o = document.createElement("option");
                o.value = String(l.levelIndex);
                o.textContent = `Overwrite level ${l.levelIndex}`;
                slotSel.appendChild(o);
            }
            box.appendChild(slotSel);

            const btnRow = document.createElement("div");
            btnRow.className = "adminSaveBtns";
            const cancelBtn = document.createElement("button");
            cancelBtn.type = "button";
            cancelBtn.className = "btn";
            cancelBtn.textContent = "Cancel";
            const okBtn = document.createElement("button");
            okBtn.type = "button";
            okBtn.className = "btn green";
            okBtn.textContent = "Publish";
            btnRow.append(cancelBtn, okBtn);
            box.appendChild(btnRow);
            wrap.appendChild(box);
            document.body.appendChild(wrap);

            const close = () => wrap.remove();
            cancelBtn.onclick = close;
            okBtn.onclick = () => {
                const v = slotSel.value;
                let idx = next;
                if (v !== "__new__") {
                    if (!canOverwrite) {
                        alert("You need Overwrite Practice Levels permission to replace an official level.");
                        return;
                    }
                    idx = Math.floor(Number(v));
                    if (!confirm(`Overwrite official practice level ${idx}?`)) return;
                } else if (!canPublish) {
                    alert("You need Publish Practice Levels permission to add a new official level.");
                    return;
                }
                void (async () => {
                    try {
                        const def = getDef();
                        await saveAdminPracticeLevel(idx, practiceLevelToJson(def), {
                            displayName: nameInp.value.trim() || null,
                            description: descInp.value.trim() || null,
                            category: catSel.value || null,
                        });
                        close();
                        opts.onLevelsChanged();
                    } catch (e) {
                        const msg = e instanceof Error ? e.message : "";
                        if (msg.includes("forbidden")) {
                            alert("Publish blocked: missing administrator permission.");
                        } else {
                            alert("Could not publish level (check admin session and database).");
                        }
                    }
                })();
            };
        })();
    }

    // ---- save draft dialog ----
    function openSaveDraftDialog() {
        const wrap = document.createElement("div");
        wrap.className = "adminSaveDialogOverlay";
        const box = document.createElement("div");
        box.className = "adminSaveDialog modeBox";
        box.innerHTML = "<h3>Save practice draft</h3>";

        const hint = document.createElement("p");
        hint.className = "accessHint";
        hint.textContent = "Shared with everyone who has Practice Editor access.";
        box.appendChild(hint);

        const nameInput = document.createElement("input");
        nameInput.type = "text";
        nameInput.className = "formInput";
        nameInput.placeholder = "Optional name (date used if empty)";
        nameInput.maxLength = 120;
        box.appendChild(nameInput);

        const btnRow = document.createElement("div");
        btnRow.className = "adminSaveBtns";
        const cancelBtn = document.createElement("button");
        cancelBtn.type = "button";
        cancelBtn.className = "btn";
        cancelBtn.textContent = "Cancel";
        const okBtn = document.createElement("button");
        okBtn.type = "button";
        okBtn.className = "btn green";
        okBtn.textContent = "Save";
        btnRow.append(cancelBtn, okBtn);
        box.appendChild(btnRow);
        wrap.appendChild(box);
        document.body.appendChild(wrap);

        const close = () => wrap.remove();
        cancelBtn.onclick = close;
        okBtn.onclick = () => {
            void (async () => {
                try {
                    await savePracticeEditorDraft(practiceLevelToJson(getDef()), nameInput.value.trim() || null);
                    close();
                } catch {
                    alert("Could not save draft (check session and database).");
                }
            })();
        };
    }

    // ---- load draft dialog ----
    function openLoadDraftDialog() {
        void (async () => {
            let drafts: PracticeEditorDraftSummary[] = [];
            try {
                drafts = await fetchPracticeEditorDrafts();
            } catch {
                alert("Could not load draft list.");
                return;
            }

            const wrap = document.createElement("div");
            wrap.className = "adminSaveDialogOverlay";
            const box = document.createElement("div");
            box.className = "adminSaveDialog modeBox adminLoadDraftDialog";
            box.innerHTML = "<h3>Load practice draft</h3>";

            if (!drafts.length) {
                const empty = document.createElement("p");
                empty.className = "accessHint";
                empty.textContent = "No saved drafts yet.";
                box.appendChild(empty);
            } else {
                const list = document.createElement("div");
                list.className = "adminDraftList";
                for (const d of drafts) {
                    const row = document.createElement("button");
                    row.type = "button";
                    row.className = "btn adminDraftListItem";
                    const label = d.name?.trim() || "(untitled)";
                    const who = d.authorDisplayName || "Unknown";
                    const when = (() => {
                        try { return new Date(d.updatedAt).toLocaleString(); } catch { return d.updatedAt; }
                    })();
                    row.innerHTML = `<strong>${label}</strong><span class="adminDraftListMeta"> — ${who}, ${when}</span>`;
                    row.onclick = () => {
                        void (async () => {
                            try {
                                const full = await fetchPracticeEditorDraft(d.id);
                                const def = normalizePracticeLevelData(full.data);
                                layoutApi.loadLevel(def);
                                applyDef(def);
                                wrap.remove();
                            } catch {
                                alert("Could not load draft.");
                            }
                        })();
                    };
                    list.appendChild(row);
                }
                box.appendChild(list);
            }

            const cancelBtn = document.createElement("button");
            cancelBtn.type = "button";
            cancelBtn.className = "btn";
            cancelBtn.textContent = "Cancel";
            cancelBtn.onclick = () => wrap.remove();
            box.appendChild(cancelBtn);
            wrap.appendChild(box);
            document.body.appendChild(wrap);
        })();
    }

    // ---- initialize ----
    const initialDef = emptyPracticeLevel();
    layoutApi.loadLevel(initialDef);
    applyDef(initialDef);

    // ---- public API ----
    return {
        destroy() {
            editorDestroyed = true;
            overlayRo?.disconnect();
            layoutApi.destroy();
            mount.replaceChildren();
        },
        loadLevel(def: PracticeLevelDef) {
            layoutApi.loadLevel(def);
            applyDef(def);
        },
        refitCanvas() {
            layoutApi.refitCanvas();
            syncOverlaySize();
        },
        getDef,
    };
}
